import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useEffect, useCallback, useState, useMemo } from "react";
import {
  DataTable,
  buildListMasterTableColumns,
} from "../../../components/data-table";
import { ListMasterPageLayout } from "../../../components/list-master";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import { Switch } from "../../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { toast } from "sonner";
import {
  donationSubCategoryMasterAPI,
  donationMainCategoryMasterAPI,
  type DonationSubCategoryMaster,
  type DonationSubCategoryMasterCreatePayload,
  type DonationMainCategoryMaster,
} from "../../../services/api";
import { CommonPagination } from "../../../components/CommonPagination";
import type { ListMasterRow } from "../../../components/data-table/listMasterTableColumns";

type DonationSubCategoryTableRow = DonationSubCategoryMaster & ListMasterRow;

type DonationSubCategoryFormState = {
  main_category: string;
  title: string;
  is_active: boolean;
};

const initialFormState: DonationSubCategoryFormState = {
  main_category: "",
  title: "",
  is_active: true,
};

function getErrorMessage(
  err: unknown,
  fallback: string = "Failed to save donation sub category"
) {
  if (err && typeof err === "object" && "response" in err) {
    const response = (
      err as {
        response?: {
          data?: { message?: string; detail?: string; error?: string };
        };
      }
    ).response;
    return (
      response?.data?.message ??
      response?.data?.detail ??
      response?.data?.error ??
      (err as unknown as Error)?.message ??
      fallback
    );
  }
  return (err as Error)?.message ?? fallback;
}

function toTableRow(
  item: DonationSubCategoryMaster
): DonationSubCategoryTableRow {
  return {
    ...item,
    name: item.title?.trim() ?? item.name?.trim() ?? "",
  };
}

function getSubCategoryTitle(row: DonationSubCategoryMaster): string {
  return row.title?.trim() ?? row.name?.trim() ?? "—";
}

type DonationSubCategoryPageProps = {
  embedded?: boolean;
};

export default function DonationSubCategoryPage({
  embedded = false,
}: DonationSubCategoryPageProps) {
  const [subCategories, setSubCategories] = useState<
    DonationSubCategoryTableRow[]
  >([]);
  const [mainCategories, setMainCategories] = useState<
    DonationMainCategoryMaster[]
  >([]);
  const [loadingMasters, setLoadingMasters] = useState(true);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] =
    useState<DonationSubCategoryFormState>(initialFormState);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editing, setEditing] = useState<DonationSubCategoryMaster | null>(
    null
  );
  const [deleting, setDeleting] = useState<DonationSubCategoryMaster | null>(
    null
  );
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const resetForm = () => {
    setForm(initialFormState);
    setEditing(null);
  };

  const loadMainCategories = useCallback(async () => {
    setLoadingMasters(true);
    try {
      const list = await donationMainCategoryMasterAPI.list();
      setMainCategories(list.filter(c => c.is_active !== false));
    } catch {
      toast.error("Failed to load main categories");
      setMainCategories([]);
    } finally {
      setLoadingMasters(false);
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await donationSubCategoryMasterAPI.listPaginated({
        page: currentPage,
        page_size: pageSize,
        search: searchTerm.trim() || undefined,
      });
      const list = response.data ?? response.results ?? [];
      const count = response.count ?? response.total_count ?? list.length;
      const computedTotalPages =
        response.total_pages ?? Math.ceil(count / pageSize);

      setSubCategories(
        (Array.isArray(list) ? list : []).map(item => toTableRow(item))
      );
      setTotalItems(count);
      setTotalPages(computedTotalPages);
    } catch {
      toast.error("Failed to load donation sub categories");
      setSubCategories([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    void loadMainCategories();
  }, [loadMainCategories]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void fetchList();
    }, 300);

    return () => clearTimeout(timer);
  }, [fetchList]);

  const handleSave = async () => {
    if (!form.main_category) {
      toast.error("Main category is required");
      return;
    }
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }

    setSubmitLoading(true);
    try {
      const payload: DonationSubCategoryMasterCreatePayload = {
        main_category: form.main_category,
        title: form.title.trim(),
        is_active: form.is_active,
      };

      if (editing) {
        await donationSubCategoryMasterAPI.update(editing.id, payload);
        toast.success("Donation sub category updated successfully");
      } else {
        await donationSubCategoryMasterAPI.create(payload);
        toast.success("Donation sub category created successfully");
      }

      setDialogOpen(false);
      resetForm();
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        void fetchList();
      }
    } catch (err: unknown) {
      toast.error(String(getErrorMessage(err)));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) {
      return;
    }

    setDeleteLoading(true);
    try {
      await donationSubCategoryMasterAPI.delete(deleting.id);
      toast.success("Donation sub category deleted successfully");
      setDeleting(null);
      if (subCategories.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        void fetchList();
      }
    } catch (err: unknown) {
      toast.error(
        String(getErrorMessage(err, "Failed to delete donation sub category"))
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEdit = useCallback((row: DonationSubCategoryTableRow) => {
    setEditing(row);
    setForm({
      main_category: String(row.main_category),
      title: getSubCategoryTitle(row),
      is_active: row.is_active ?? true,
    });
    setDialogOpen(true);
  }, []);

  const resolveMainCategoryName = useCallback(
    (row: DonationSubCategoryMaster) => {
      if (row.main_category_name?.trim()) {
        return row.main_category_name.trim();
      }
      const match = mainCategories.find(
        c => String(c.id) === String(row.main_category)
      );
      return match?.name ?? String(row.main_category);
    },
    [mainCategories]
  );

  const tableColumns = useMemo(
    () =>
      buildListMasterTableColumns<DonationSubCategoryTableRow>({
        currentPage,
        pageSize,
        onEdit: openEdit,
        onDelete: setDeleting,
        showCode: false,
        nameColumnLabel: "Title",
        renderNameCell: row => (
          <span className="font-medium">{getSubCategoryTitle(row)}</span>
        ),
        extraColumns: [
          {
            id: "main_category",
            label: "Main Category",
            width: 180,
            renderCell: row => resolveMainCategoryName(row),
          },
        ],
      }),
    [currentPage, pageSize, openEdit, resolveMainCategoryName]
  );

  return (
    <div className={embedded ? "" : "p-6"}>
      {!embedded && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground">
            Donation Sub Category
          </h1>
          <p className="text-muted-foreground">
            Manage donation sub categories linked to main categories
          </p>
        </div>
      )}
      <ListMasterPageLayout
        tableId="list-master-donation-sub-category"
        tableColumns={tableColumns}
        listTitle={`Donation Sub Categories (${totalItems})`}
        searchPlaceholder="Search donation sub categories..."
        searchTerm={searchTerm}
        onSearchChange={v => {
          setSearchTerm(v);
          setCurrentPage(1);
        }}
        headerActions={
          <Dialog
            open={dialogOpen}
            onOpenChange={open => {
              setDialogOpen(open);
              if (!open) {
                resetForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create new
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editing
                    ? "Edit Donation Sub Category"
                    : "Create New Donation Sub Category"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Main Category *</Label>
                  <Select
                    value={form.main_category || undefined}
                    onValueChange={v =>
                      setForm(prev => ({ ...prev, main_category: v }))
                    }
                    disabled={loadingMasters}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select main category" />
                    </SelectTrigger>
                    <SelectContent>
                      {mainCategories.map(c => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="donation-sub-category-title">Title *</Label>
                  <Input
                    id="donation-sub-category-title"
                    value={form.title}
                    onChange={e =>
                      setForm(prev => ({ ...prev, title: e.target.value }))
                    }
                    placeholder="School Building"
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
                  <div>
                    <Label htmlFor="donation-sub-category-active">Status</Label>
                    <p className="text-xs text-muted-foreground">
                      {form.is_active ? "Active" : "Inactive"}
                    </p>
                  </div>
                  <Switch
                    id="donation-sub-category-active"
                    checked={form.is_active}
                    onCheckedChange={checked =>
                      setForm(prev => ({ ...prev, is_active: checked }))
                    }
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    disabled={submitLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => void handleSave()}
                    disabled={submitLoading}
                  >
                    {submitLoading ? (
                      <CustomLoader size="xs" />
                    ) : editing ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        }
      >
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <CustomLoader size="md" />
          </div>
        ) : (
          <DataTable
            embedded
            hideToolbar
            tableId="list-master-donation-sub-category"
            columns={tableColumns}
            data={subCategories}
            getRowKey={d => d.id}
            emptyMessage={
              totalItems === 0 && !searchTerm.trim()
                ? "No donation sub categories yet. Create one to get started."
                : "No donation sub categories found matching your search."
            }
          />
        )}

        {totalItems > 0 && (
          <div className="mt-4 border-t pt-4">
            <CommonPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalCount={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={size => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </ListMasterPageLayout>

      <Dialog
        open={!!deleting}
        onOpenChange={open => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Donation Sub Category</DialogTitle>
          </DialogHeader>
          {deleting && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete{" "}
                <strong>{getSubCategoryTitle(deleting)}</strong>?
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleting(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => void handleDelete()}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? <CustomLoader size="xs" /> : "Delete"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
